import {
  CombinedGraphQLErrors,
  UnconventionalError,
} from '@apollo/client/errors';

export interface RequestError extends Error {
  status_code?: string | number | null;
  message_translation_key?: string;
}

/** Preserve backend metadata when Apollo delivers a direct or combined error. */
export function getRequestError(error: unknown): RequestError {
  if (UnconventionalError.is(error)) return getRequestError(error.cause);
  if (CombinedGraphQLErrors.is(error)) {
    const backendError = error.errors.find(
      (item) => 'status_code' in item || item.extensions?.status_code != null,
    );
    if (backendError) {
      return Object.assign(
        new Error(backendError.message),
        backendError.extensions,
        backendError,
      );
    }
    return error;
  }
  // Also accept errors from integrations still using Apollo Client 3's shape.
  if (
    error &&
    typeof error === 'object' &&
    'networkError' in error &&
    error.networkError
  ) {
    return getRequestError(error.networkError);
  }
  if (error instanceof Error) return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const normalized = Object.assign(new Error(String(error.message)), error);
    // DOMException and cross-realm Error properties need not be enumerable.
    if ('name' in error && typeof error.name === 'string') {
      normalized.name = error.name;
    }
    return normalized;
  }
  return new Error(typeof error === 'string' ? error : 'Error general');
}

export function isRequestAborted(error: unknown): boolean {
  return getRequestError(error).name === 'AbortError';
}
